import React from 'react'

import { SELF_SERVE_CANCELLATION } from '@disco/constants/FeatureTypes'
import { Dialog, Text, Flex, Button } from 'components/ds'
import Icon from 'components/ui/icon'
import InlineLink from 'components/ui/inline-link'
import { useSelector } from 'hooks/use-redux'
import { getUser } from '@disco/state-management/selectors/user'
import { useFeatureEnabled } from '@disco/state-management/selectors/business'
import { getSubscriptionExpiresAt } from '@disco/state-management/selectors/subscription'
import { formatDate } from '@disco/utils/time-utils'
import { CancelSubscriptionNeedAssistanceLink } from './CancelSubscriptionNeedAssistanceLink'

type CancelSubscriptionModalStepOneProps = {
  onNext: () => void
  /** Replaces the legacy plans link, e.g. with Billing and usage's Chargebee link; null leaves the line out */
  changePlanLink?: React.ReactNode
}

/**
 * First step of the in-app cancellation: warns what the business loses and offers to change plan
 * instead, through the legacy plans link unless `changePlanLink` replaces it.
 */
export const CancelSubscriptionModalStepOne = ({
  onNext,
  changePlanLink = (
    <InlineLink to="/settings/billing/plans">change your plan</InlineLink>
  ),
}: CancelSubscriptionModalStepOneProps) => {
  const user = useSelector(getUser)
  const inArrears = user?.business?.private_metadata?.payment_overdue
  const subscriptionExpiresAt = useSelector(getSubscriptionExpiresAt)
  const selfServePlanChange = useFeatureEnabled(SELF_SERVE_CANCELLATION)
  const formattedSubscriptionExpiresAt = formatDate(subscriptionExpiresAt)

  const hostname = user?.business?.hostname

  let noCancellationsDueTo = ''

  if (!selfServePlanChange) noCancellationsDueTo += ' SELF_SERVE_CANCELLATIONS'
  if (user?.business?.private_metadata?.plan_on_contract)
    noCancellationsDueTo += ' plan_on_contract'

  let content = (
    <>
      <Dialog.Description mb="4">
        <Text>
          By cancelling, after {formattedSubscriptionExpiresAt} you will lose
          access to <b>all tracks</b> and all of the content saved to your DISCO
          account.
        </Text>
      </Dialog.Description>
      <p>
        <Text>
          Please download any files you require prior to{' '}
          {formattedSubscriptionExpiresAt}.
        </Text>
      </p>
      {changePlanLink && (
        <p>
          <Text>You can also {changePlanLink}</Text>
        </p>
      )}
    </>
  )

  if (inArrears) {
    content = (
      <>
        <Dialog.Description mb="4">
          <Text>
            By cancelling, you will immediately lose access to <b>All tracks</b>{' '}
            and all of the content saved to your DISCO account.
          </Text>
        </Dialog.Description>
        <p>
          <Text>
            Please download any files you require prior to deleting your
            account.
          </Text>
        </p>
        <p>
          <CancelSubscriptionNeedAssistanceLink />
        </p>
      </>
    )
  } else if (noCancellationsDueTo) {
    content = (
      <Dialog.Description mb="4">
        <Text>
          As part of your enterprise plan, cancellations are handled directly by
          your account manager. To proceed, please get in touch with them and
          they’ll guide you through the process.
        </Text>
      </Dialog.Description>
    )
  }

  return (
    <>
      <Dialog.Title mt="0">
        <Flex justify="between">
          <Flex align="center" gap="2">
            <Icon type="warning" color="#FD7D69" />
            <Text weight="medium" size="5">
              Are you sure you want to cancel?
            </Text>
          </Flex>
          <Dialog.Close>
            <Button color="gray" variant="ghost">
              <Icon type="close" />
            </Button>
          </Dialog.Close>
        </Flex>
      </Dialog.Title>

      {content}

      {noCancellationsDueTo ? (
        <p>
          <a
            className="inline-link"
            href={`mailto:sales@disco.ac?subject=Cancellation%20request:%20${hostname}`}
          >
            Contact your account manager
          </a>
        </p>
      ) : (
        <Flex gap="3" mt="4" justify="end">
          <Button color="red" onClick={onNext}>
            {'Proceed to cancellation'}
          </Button>
        </Flex>
      )}
    </>
  )
}
